import { Item } from "@dto/architecture.dto";
import { Form } from "antd";
import { NamePath } from "antd/es/form/interface";
import { DefaultOptionType, SelectProps } from "antd/es/select";
import { PropsWithChildren, useCallback, useMemo, useState, type JSX } from "react";
import { XmSelect } from "src/component/select/XmSelect";
import { GLOB } from "src/util/Glob";
import TagsList, { TagItem } from "../comp/TagsList";
import TreeTitle from "../comp/TreeTitle";

const formatLabel = (item: Item) => item.root_parent?.label ? `${item.label} (${item.root_parent.label})` : item.label;

interface Props<FormItem> extends PropsWithChildren {
  makeItemOfList: (item: FormItem) => TagItem,
  makeLabel: (item: Item) => string | JSX.Element,
  isLoading: boolean,
  treeItems: Item[],
  handleCheckItem: (item: Item) => FormItem;
  treeSelectProps?: SelectProps;
}

function ModalSelectItems<FormItem extends { item_id: string; }, FormType extends { items: FormItem[]; }>({
  makeItemOfList,
  makeLabel,
  isLoading,
  treeItems,
  children,
  handleCheckItem,
  treeSelectProps,
}: Props<FormItem>) {
  const form = Form.useFormInstance<FormType>();
  const items = Form.useWatch<FormItem[]>('items', { form, preserve: true }); // ! in ant.design v5 fully
  const [openDropdown, setOpenDropdown] = useState(false);
  const handleRemoveItem = (id: string) => () => form.setFieldValue('items' as NamePath<FormType>, items.filter(item => item.item_id !== id));
  const [searchValue, setSearchValue] = useState('');
  // ! select dropdown is closing when selected same item twice in a row
  // const handleSelect = (item: Item) => (e: CheckboxChangeEvent) => {
  //   form.setFieldValue('items', e.target.checked ? [...items, handleCheckItem(item)] : items.filter(it => item.item_id !== it.item_id));
  //   e.preventDefault();
  // };

  const handleDropdownClick: React.MouseEventHandler<HTMLDivElement> = e => {
    const cls = (e.target as Element).getAttribute('class');
    if (!cls || cls.includes('ant-checkbox-input') || cls.includes('xm-item-root-parent'))
      return;

    e.preventDefault();
  };

  const handleBlur = () => setOpenDropdown(false);
  const handleClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    setOpenDropdown(true);
    e.preventDefault(); // this will prevent additional blur call in some browsers (bug)
  };

  const handleSelect = (_: string, { value: id }: DefaultOptionType) => {
    const selectedItem = treeItems.find(item => item.item_id === id);
    form.setFieldValue('items' as NamePath, items.some(i => i.item_id == id) ? items.filter(it => id !== it.item_id) : [...items, handleCheckItem(selectedItem)]);
  };

  const handleSearch = (value: string) => setSearchValue(value);

  const sortedItems = useMemo(
    () => treeItems
      .map(item => ({ item, label: formatLabel(item) }))
      .sort((a, b) => GLOB.naturalSort(a.label, b.label)),
    [treeItems]
  );

  const selectedIds = useMemo(() => new Set(items?.map(({ item_id }) => item_id)), [items]);

  const options = useMemo(
    () => sortedItems.map(({ item, label }): DefaultOptionType => ({
      value: item.item_id,
      label,
      search: label,
      itemLabel: makeLabel(item),
    })),
    [sortedItems, makeLabel]
  );

  const optionRender: SelectProps['optionRender'] = useCallback(
    ({ value, data }) => <TreeTitle checked={selectedIds.has(value as string)} label={data.itemLabel} />,
    [selectedIds]
  );

  return <>
    <div className='xm-select-controls'>
      {children}
      <XmSelect
        className="xm-tree-select"
        open={openDropdown}
        {...treeSelectProps}
        value={null} // it keeps placeholder, after item select
        loading={isLoading}
        disabled={isLoading || treeSelectProps?.disabled}
        size='middle'
        style={{ flex: 1 }}
        placeholder="Search"
        showSearch={{ optionFilterProp: 'search', searchValue, onSearch: handleSearch }}
        options={options}
        optionRender={optionRender}
        onClick={handleClick}
        onBlur={handleBlur}
        onSelect={handleSelect}
        popupMatchSelectWidth={false}
        classNames={{ root: 'xm-select', popup: { root: 'xm-select-dropdown xm-tree-select-popup' } }}
        getPopupContainer={trigger => trigger.closest('.xm-select-controls') ?? document.body}
        popupRender={menu => (
          <div onClick={handleDropdownClick}>
            {menu}
          </div>
        )}
        ignoreReadonly
      />
    </div>
    <TagsList
      items={items?.map(makeItemOfList).sort((a, b) => GLOB.naturalSort(a.title, b.title))}
      onClose={handleRemoveItem}
    />
  </>;
}

export default ModalSelectItems;